'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

interface DebugInfo {
  timestamp: string;
  url: string;
  params: Record<string, string>;
  referrer: string;
  userAgent: string;
  customerId?: string;
  isWebinapp: boolean;
  addressFromUrl?: {
    lat: string;
    lng: string;
    success: boolean;
    error?: string;
  };
}

interface DebugTrackerContextType {
  debugLogs: DebugInfo[];
  addDebugLog: (info: Partial<DebugInfo>) => void;
  clearLogs: () => void;
  forceHideDebug: () => void;
  isTargetUser: boolean;
  showDebugger: boolean;
  setShowDebugger: (show: boolean) => void;
}

const DebugTrackerContext = createContext<DebugTrackerContextType | undefined>(
  undefined
);

// Customer ID cần debug (theo yêu cầu)
const TARGET_CUSTOMER_ID = '68763db4b94e557bca1d75ff';

export function DebugTrackerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [debugLogs, setDebugLogs] = useState<DebugInfo[]>([]);
  const [isTargetUser, setIsTargetUser] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);

  // Load debug logs from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem('ahafood_debug_logs');
      if (stored) {
        const logs = JSON.parse(stored) as DebugInfo[];
        setDebugLogs(logs);
      }
    } catch (error) {
      console.error('Failed to load debug logs:', error);
    }
  }, []);

  // Save debug logs to localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem('ahafood_debug_logs', JSON.stringify(debugLogs));
    } catch (error) {
      console.error('Failed to save debug logs:', error);
    }
  }, [debugLogs]);

  // Clear debug logs when JSFunction.call({ name: 'close' }) is called
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Monitor for JSFunction close calls by intercepting the global JSFunction
    const originalJSFunction = (window as any).JSFunction;
    if (originalJSFunction && typeof originalJSFunction.call === 'function') {
      (window as any).JSFunction = {
        ...originalJSFunction,
        call: (params: any) => {
          // Clear debug logs when webinapp is being closed
          if (params?.name === 'close') {
            localStorage.removeItem('ahafood_debug_logs');
            console.log('🔧 Debug logs cleared - WebInApp close detected');
          }
          // Always call the original function
          return originalJSFunction.call(params);
        },
      };
    }

    return () => {
      // Restore original JSFunction if it was modified
      if (originalJSFunction) {
        (window as any).JSFunction = originalJSFunction;
      }
    };
  }, []);

  // Check if current user is target user
  useEffect(() => {
    const checkTargetUser = () => {
      // Check from cookies - if no ws_token, user is logged out
      const cookies = document.cookie.split(';');
      let hasWSToken = false;

      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'ws_token' && value) {
          hasWSToken = true;
          break;
        }
      }

      // If no token, user is logged out - hide debug
      if (!hasWSToken) {
        setIsTargetUser(false);
        return;
      }

      // Check from latest debug log with customerId
      const latestLogWithCustomerId = debugLogs
        .slice()
        .reverse()
        .find((log) => log.customerId);

      // Set target user status based on customer ID match
      setIsTargetUser(
        latestLogWithCustomerId?.customerId === TARGET_CUSTOMER_ID
      );
    };

    checkTargetUser();

    // Also check every 5 seconds to catch logout events
    const interval = setInterval(checkTargetUser, 5000);

    return () => clearInterval(interval);
  }, [debugLogs]);

  const addDebugLog = useCallback((info: Partial<DebugInfo>) => {
    if (typeof window === 'undefined') return;

    const newLog: DebugInfo = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      params: Object.fromEntries(new URLSearchParams(window.location.search)),
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      isWebinapp: localStorage.getItem('isWebinapp') === 'true',
      ...info,
    };

    setDebugLogs((prev) => {
      const updated = [...prev, newLog];
      // Keep only last 50 logs to prevent memory issues
      return updated.slice(-50);
    });

    // Check if this log contains target customer ID
    if (info.customerId === TARGET_CUSTOMER_ID) {
      setIsTargetUser(true);
    }
  }, []);

  const clearLogs = useCallback(() => {
    setDebugLogs([]);
    setIsTargetUser(false); // Also hide debug panel
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ahafood_debug_logs');
    }
  }, []);

  // Export clear function globally so it can be called from anywhere
  useEffect(() => {
    if (typeof window === 'undefined') return;

    (window as any).clearAhafoodDebugLogs = clearLogs;

    return () => {
      delete (window as any).clearAhafoodDebugLogs;
    };
  }, [clearLogs]);

  const forceHideDebug = useCallback(() => {
    setIsTargetUser(false);
    setShowDebugger(false);
  }, []);

  const value: DebugTrackerContextType = {
    debugLogs,
    addDebugLog,
    clearLogs,
    forceHideDebug,
    isTargetUser,
    showDebugger,
    setShowDebugger,
  };

  return (
    <DebugTrackerContext.Provider value={value}>
      {children}
    </DebugTrackerContext.Provider>
  );
}

export function useDebugTracker() {
  const context = useContext(DebugTrackerContext);
  if (context === undefined) {
    throw new Error('useDebugTracker must be used within DebugTrackerProvider');
  }
  return context;
}

// Export a direct function that can be called from anywhere
export const clearDebugLogs = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('ahafood_debug_logs');
    console.log('🔧 Debug logs cleared directly');

    // Also call the window function if available
    if ((window as any).clearAhafoodDebugLogs) {
      (window as any).clearAhafoodDebugLogs();
    }
  }
};
